import { escapeHTML } from '../../utils/escape.js';
import { formatMonto } from '../../utils/formatters.js';

/**
 * Cuerpo del cajón "Registrar pago" (S5b, design.md AD7/AD13/AD14, spec
 * `payroll-payments` "Register Payment Drawer"). Función de render pura —
 * escapa TODOS sus propios valores interpolados (AD7: el shell sólo escapa
 * el título). Muestra neto/ya pagado/saldo, alta de un pago parcial (fecha,
 * medio, monto pre-cargado con el saldo, nota) y la lista activa/anulada —
 * MISMO patrón que `AdelantoDrawerView.js`, ya `pagadoCentavos`/
 * `saldoCentavos` vienen ya resueltos por `js/admin.js` vía
 * `estadoPago` (`liquidacion-pagos-policy.js`) — esta vista sólo formatea.
 *
 * `fecha` de la columna `date` de Postgres — mismo formateador string-only
 * que `AdelantoDrawerView.js#formatFechaSql` (duplicado deliberado, no
 * exportado desde un componente de vista hacia otro).
 * @param {string|null|undefined} fechaSql
 * @returns {string}
 */
function formatFechaSql(fechaSql) {
    if (typeof fechaSql !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaSql)) return '—';
    const [anio, mes, dia] = fechaSql.split('-');
    return `${dia}/${mes}/${anio}`;
}

const MEDIO_LABELS = { efectivo: 'Efectivo', transferencia: 'Transferencia' };

const BTN = 'px-3 py-2 rounded-xl bg-brand text-white text-[11px] font-black uppercase tracking-widest shadow disabled:opacity-60 disabled:pointer-events-none';
const BTN_SECONDARY = 'px-3 py-1.5 rounded-xl border border-border-subtle text-text-muted text-[10px] font-black uppercase tracking-widest hover:text-text-primary';
const INPUT = 'w-full bg-base border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-brand';

/**
 * @param {{
 *   empleadoNombre: string,
 *   neto: number,
 *   pagadoCentavos?: number,
 *   saldoCentavos?: number,
 *   fecha?: string, medio?: string, monto?: string, nota?: string,
 *   cargando: boolean,
 *   activos: Array<{id:string, fecha:string, medio:string, monto:number, nota:string|null}>,
 *   anulados: Array<{id:string, fecha:string, medio:string, monto:number, nota:string|null, anulado_motivo:string|null}>,
 *   error?: string|null,
 *   submitting?: boolean,
 *   anulandoId?: string|null,
 *   motivo?: string,
 *   anularError?: string|null,
 *   incierto?: boolean,
 *   mensaje?: string|null,
 *   soloLectura?: boolean,
 * }} model
 * `soloLectura` (L5): la liquidación está anulada — no se registran pagos
 * nuevos (`registrar_pago` los rechaza), sólo se pueden anular los activos.
 * @returns {string}
 */
export function PagoDrawerView(model = {}) {
    const activos = model.activos ?? [];
    const anulados = model.anulados ?? [];
    const medio = model.medio ?? 'efectivo';

    const filaActiva = (p) => `
        <li class="flex items-center justify-between gap-2 py-2 border-b border-border-subtle last:border-0" data-pago-activo data-id="${escapeHTML(p.id)}">
            <span class="text-xs text-text-primary">${formatFechaSql(p.fecha)} · ${formatMonto(p.monto)} · ${escapeHTML(MEDIO_LABELS[p.medio] ?? p.medio ?? '')}${p.nota ? ` · ${escapeHTML(p.nota)}` : ''}</span>
            ${
                model.anulandoId === p.id
                    ? `
                <form data-action-form="pago-anular-confirmar" data-id="${escapeHTML(p.id)}" class="flex items-center gap-2">
                    <input type="text" name="motivo" value="${escapeHTML(model.motivo ?? '')}" required class="${INPUT}" placeholder="Motivo" />
                    <button type="submit" class="${BTN_SECONDARY}">Confirmar</button>
                    <button type="button" data-action="pago-anular-cancelar" class="${BTN_SECONDARY}">Cancelar</button>
                </form>
            `
                    : `<button type="button" data-action="pago-anular-abrir" data-id="${escapeHTML(p.id)}" class="${BTN_SECONDARY}">Anular</button>`
            }
        </li>
    `;

    const filaAnulada = (p) => `
        <li class="py-2 border-b border-border-subtle last:border-0 text-xs text-text-muted line-through" data-pago-anulado data-id="${escapeHTML(p.id)}">
            ${formatFechaSql(p.fecha)} · ${formatMonto(p.monto)} · ${escapeHTML(MEDIO_LABELS[p.medio] ?? p.medio ?? '')} — ${escapeHTML(p.anulado_motivo ?? '')}
        </li>
    `;

    return `
        <div data-drawer-tipo="pago">
            <p class="text-xs font-bold text-text-muted mb-4">${escapeHTML(model.empleadoNombre ?? '')}</p>

            <dl class="grid grid-cols-3 gap-2 mb-4 text-center" data-pago-resumen>
                <div>
                    <dt class="text-[10px] font-black uppercase tracking-widest text-text-muted">Neto</dt>
                    <dd class="text-sm font-bold text-text-primary">${formatMonto(model.neto ?? 0)}</dd>
                </div>
                <div>
                    <dt class="text-[10px] font-black uppercase tracking-widest text-text-muted">Ya pagado</dt>
                    <dd class="text-sm font-bold text-text-primary">${formatMonto((model.pagadoCentavos ?? 0) / 100)}</dd>
                </div>
                <div>
                    <dt class="text-[10px] font-black uppercase tracking-widest text-text-muted">Saldo</dt>
                    <dd class="text-sm font-bold text-text-primary">${formatMonto((model.saldoCentavos ?? 0) / 100)}</dd>
                </div>
            </dl>

            ${model.error ? `<div role="alert" class="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-bold">${escapeHTML(model.error)}</div>` : ''}
            ${model.anularError ? `<div role="alert" class="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-bold">${escapeHTML(model.anularError)}</div>` : ''}

            ${
                model.soloLectura && !model.incierto
                    ? '<p role="note" class="mb-6 text-xs text-text-muted" data-pago-solo-lectura>La liquidación está anulada: no se registran pagos nuevos. Podés anular los que siguen activos.</p>'
                    : model.incierto
                    ? `
            <div role="alert" class="mb-6 px-3 py-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs font-bold" data-pago-incierto>
                <p class="mb-2">${escapeHTML(model.mensaje ?? '')}</p>
                <button type="button" data-action="tablero-pago-actualizar" class="${BTN}">Actualizar lista</button>
            </div>
            `
                    : `
            <form data-action-form="pago-registrar" class="space-y-3 mb-6">
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Fecha
                    <input type="date" name="fecha" value="${escapeHTML(model.fecha ?? '')}" required class="${INPUT} mt-1" />
                </label>
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Medio
                    <select name="medio" required class="${INPUT} mt-1">
                        <option value="efectivo" ${medio === 'efectivo' ? 'selected' : ''}>Efectivo</option>
                        <option value="transferencia" ${medio === 'transferencia' ? 'selected' : ''}>Transferencia</option>
                    </select>
                </label>
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Monto
                    <input type="number" name="monto" step="0.01" min="0.01" value="${escapeHTML(model.monto ?? '')}" required class="${INPUT} mt-1" />
                </label>
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Nota (opcional)
                    <input type="text" name="nota" value="${escapeHTML(model.nota ?? '')}" class="${INPUT} mt-1" />
                </label>
                <button type="submit" class="${BTN}" ${model.submitting ? 'disabled' : ''}>Registrar pago</button>
            </form>
            `
            }

            <div>
                <h3 class="text-[11px] font-black uppercase tracking-widest text-text-muted mb-2">Pagos activos</h3>
                ${model.cargando ? '<p class="text-xs text-text-muted">Cargando...</p>' : activos.length ? `<ul>${activos.map(filaActiva).join('')}</ul>` : '<p class="text-xs text-text-muted">Sin pagos activos.</p>'}
            </div>

            ${
                anulados.length
                    ? `
                <div class="mt-4">
                    <h3 class="text-[11px] font-black uppercase tracking-widest text-text-muted mb-2">Anulados</h3>
                    <ul>${anulados.map(filaAnulada).join('')}</ul>
                </div>
            `
                    : ''
            }
        </div>
    `;
}
